import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, from, timer, Observable } from 'rxjs';
import { switchMap, map, catchError, concatMap } from 'rxjs/operators';
import { Action } from '@ngrx/store';

import { PaymentActions } from '@store/actions/payment.actions';
import { PaymentService } from '@services/payment.service';
import { AuthService } from '@services/auth.service';

@Injectable()
export class PaymentEffects {
  private actions$ = inject(Actions);
  private paymentService = inject(PaymentService);
  private auth = inject(AuthService);

  // ── Load /api/payment-config ───────────────────────────────────
  loadConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PaymentActions.loadConfig),
      switchMap(() =>
        from(this.paymentService.fetchConfig()).pipe(
          map(cfg => PaymentActions.loadConfigSuccess(cfg)),
          catchError(err =>
            of(PaymentActions.loadConfigFailure({ error: err?.message ?? 'Config unavailable' }))
          ),
        ),
      ),
    ),
  );

  // ── Check whether the logged-in user has paid ──────────────────
  checkHasPaid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PaymentActions.checkHasPaid),
      switchMap(() =>
        from(this.paymentService.hasPaid()).pipe(
          map(hasPaid => PaymentActions.checkHasPaidSuccess({ hasPaid })),
          catchError(err =>
            of(PaymentActions.checkHasPaidFailure({ error: err?.message ?? 'Check failed' }))
          ),
        ),
      ),
    ),
  );

  // ── Post-checkout verification: poll payments table ────────────
  // Polls immediately, then waits 2 s between retries (max 6 attempts = ~10 s).
  // Uses recursive defer so each retry only fires after the previous query resolves.
  verifyPayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PaymentActions.verifyPayment),
      switchMap(() => {
        if (!this.auth.user) {
          return of(PaymentActions.verifyPaymentNoSession());
        }
        return this._pollHasPaid(6).pipe(
          map(paid =>
            paid ? PaymentActions.verifyPaymentSuccess() : PaymentActions.verifyPaymentNotFound()
          ),
          catchError(err =>
            of(PaymentActions.verifyPaymentFailure({ error: err?.message ?? 'Verification failed' }))
          ),
        );
      }),
    ),
  );

  /** Recursive poll: check → if not paid wait 2 s → retry up to `remaining` times. */
  private _pollHasPaid(remaining: number): Observable<boolean> {
    return from(this.paymentService.hasPaid()).pipe(
      concatMap(paid => {
        if (paid) return of(true);
        if (remaining <= 1) return of(false);
        // Wait 2 s then recurse
        return timer(2000).pipe(
          concatMap(() => this._pollHasPaid(remaining - 1)),
        );
      }),
    );
  }
}
